// Repository: folder tree (left), folder contents (right) with subfolder tiles, documents table, drag & drop move, detail drawer.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { navigate, useAuth } from '../store';
import type { Doc, Folder, FolderView } from '../types';
import { ColumnHeader, Confirm, EmptyState, Field, Modal, Stamp, StatusChip, fmtDateTime, useToast } from '../ui';
import { Icon } from '../ui/Icon';
import { DocumentDrawer, SharePanel } from './DocumentDrawer';
import { DocumentForm } from './DocumentForm';
import { useBulkSign } from './Signatures';

interface TreeNode extends Folder { children: TreeNode[] }
type SortKey = 'reg' | 'title' | 'type' | 'status' | 'assigned' | 'created';
type Dialog = { kind: 'new' } | { kind: 'rename' } | { kind: 'delete' } | { kind: 'share' } | { kind: 'doc' } | null;

const DRAG_MIME = 'application/x-docflow-doc';
/** Postgres emits offsets like `+03`; Date() wants `+03:00`. */
const fmtWhen = (iso?: string | null) => fmtDateTime(iso ? iso.replace(/([+-]\d{2})$/, '$1:00') : iso);

function buildTree(list: Folder[]): TreeNode[] {
  const byId = new Map<number, TreeNode>();
  list.forEach(f => byId.set(f.id, { ...f, children: [] }));
  const roots: TreeNode[] = [];
  byId.forEach(n => {
    const parent = n.parentId != null ? byId.get(n.parentId) : undefined;
    if (parent) parent.children.push(n); else roots.push(n);
  });
  const sortRec = (nodes: TreeNode[]) => { nodes.sort((a, b) => Number(b.system) - Number(a.system) || a.name.localeCompare(b.name, 'ro')); nodes.forEach(n => sortRec(n.children)); };
  sortRec(roots);
  return roots;
}

export function Repository({ folderId, openId, onOpen }: { folderId: number | null; openId: number | null; onOpen: (id: number | null) => void }) {
  const toast = useToast();
  const { isAdmin, canRegister } = useAuth();
  const [tree, setTree] = useState<Folder[]>([]);
  const [view, setView] = useState<FolderView | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const [dialog, setDialog] = useState<Dialog>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'created', dir: 'desc' });
  const [dropTarget, setDropTarget] = useState<number | null>(null);

  const folderById = useMemo(() => { const m = new Map<number, Folder>(); tree.forEach(f => m.set(f.id, f)); return m; }, [tree]);
  const nodes = useMemo(() => buildTree(tree), [tree]);

  const loadTree = useCallback(async () => {
    try { setTree(await api.get<Folder[]>('/api/repository/tree')); }
    catch (e: any) { toast(e.message, 'error'); }
  }, []);

  const loadFolder = useCallback(async () => {
    if (folderId == null) { setView(null); return; }
    setLoading(true);
    try { setView(await api.get<FolderView>('/api/repository/folders/' + folderId)); }
    catch (e: any) {
      toast(e.status === 403 ? 'Nu aveți acces la acest director.' : e.status === 404 ? 'Directorul nu există.' : e.message, 'error');
      if (e.status === 403 || e.status === 404) navigate('/repository');
    } finally { setLoading(false); }
  }, [folderId]);

  const reload = useCallback(() => { loadTree(); loadFolder(); }, [loadTree, loadFolder]);

  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => { loadFolder(); setSort({ key: 'created', dir: 'desc' }); }, [loadFolder]);

  // auto-expand the path to the selected folder
  useEffect(() => {
    if (folderId == null || !folderById.size) return;
    setExpanded(prev => {
      const next = new Set(prev);
      let cur = folderById.get(folderId);
      while (cur) { next.add(cur.id); cur = cur.parentId != null ? folderById.get(cur.parentId) : undefined; }
      return next;
    });
  }, [folderId, folderById]);

  const toggle = (id: number) => setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // ---- drag & drop of documents onto folders
  const onDragStart = (e: React.DragEvent, d: Doc) => {
    e.dataTransfer.setData(DRAG_MIME, String(d.id));
    e.dataTransfer.setData('text/plain', d.title);
    e.dataTransfer.effectAllowed = 'move';
  };
  const isDocDrag = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes(DRAG_MIME);
  const onDragOver = (e: React.DragEvent, targetId: number) => {
    if (!isDocDrag(e) || targetId === folderId) return;
    e.preventDefault(); e.dataTransfer.dropEffect = 'move';
    if (dropTarget !== targetId) setDropTarget(targetId);
  };
  const onDrop = async (e: React.DragEvent, target: Folder) => {
    setDropTarget(null);
    if (!isDocDrag(e)) return;
    e.preventDefault();
    const id = Number(e.dataTransfer.getData(DRAG_MIME));
    if (!id || target.id === folderId) return;
    try {
      await api.put('/api/documents/' + id, { folderId: target.id });
      toast('Mutat în „' + target.name + '”', 'ok');
      reload();
    } catch (err: any) { toast(err.message, 'error'); }
  };

  // ---- folder actions
  const createFolder = async (name: string) => {
    const f = await api.post<Folder>('/api/repository/folders', { parentId: folderId, name });
    toast('Director creat', 'ok'); setDialog(null); await loadTree(); navigate('/repository/' + f.id);
  };
  const renameFolder = async (name: string) => {
    await api.put('/api/repository/folders/' + folderId, { name });
    toast('Director redenumit', 'ok'); setDialog(null); reload();
  };
  const deleteFolder = async () => {
    if (!view) return;
    try {
      await api.del('/api/repository/folders/' + view.id);
      toast('Director șters', 'ok'); setDialog(null); await loadTree();
      navigate(view.parentId != null ? '/repository/' + view.parentId : '/repository');
    } catch (e: any) { toast(e.message, 'error'); }
  };

  // ---- documents table
  const [filters, setFilters] = useState<Record<string, string>>({});
  const setFilter = (k: string, v: string) => setFilters(f => { const n = { ...f }; if (v) n[k] = v; else delete n[k]; return n; });
  useEffect(() => { setFilters({}); }, [folderId]);
  const docs = useMemo(() => {
    const norm = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const text = (d: Doc, k: string): string => {
      switch (k) {
        case 'reg': return d.regFull || '';
        case 'title': return d.title || '';
        case 'type': return d.typeName || '';
        case 'status': return d.status;
        case 'assigned': return [d.assignedDepartmentName, d.assignedUserName].filter(Boolean).join(' ');
        case 'created': return fmtWhen(d.createdAt);
        default: return '';
      }
    };
    const list = (view ? [...view.documents] : []).filter(d => Object.entries(filters).every(([k, v]) => k === 'status' ? d.status === v : norm(text(d, k)).includes(norm(v))));
    const dir = sort.dir === 'asc' ? 1 : -1;
    const val = (d: Doc): string | number => {
      switch (sort.key) {
        case 'reg': return d.regYear != null && d.regNumber != null ? d.regYear * 1e6 + d.regNumber : -1;
        case 'title': return d.title || '';
        case 'type': return d.typeName || '';
        case 'status': return d.status;
        case 'assigned': return (d.assignedUserName || d.assignedDepartmentName || '');
        default: return d.createdAt || '';
      }
    };
    return list.sort((a, b) => { const x = val(a), y = val(b); return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'ro')) * dir; });
  }, [view, sort, filters]);
  const toggleSort = (key: SortKey) => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'reg' || key === 'created' ? 'desc' : 'asc' });
  const Th = ({ k, label, options }: { k: SortKey; label: string; options?: { value: string; label: string }[] }) => (
    <ColumnHeader label={label} sortDir={sort.key === k ? sort.dir : null} onSort={() => toggleSort(k)} options={options}
      filter={filters[k] || ''} onFilter={v => setFilter(k, v)} />
  );

  const canManage = folderId == null ? isAdmin || canRegister : !!view?.canManage;
  const subfolders: Folder[] = folderId == null ? nodes : (view?.folders || []);
  const current = view && view.id === folderId ? view : null;

  const renderNode = (n: TreeNode, depth: number) => {
    const open = expanded.has(n.id);
    const active = n.id === folderId;
    return (
      <li key={n.id}>
        <div className={'node ' + (active ? 'active ' : '') + (dropTarget === n.id ? 'drop' : '')} style={{ paddingLeft: 8 + depth * 14 }}
          onClick={() => navigate('/repository/' + n.id)} title={n.path}
          onDragOver={e => onDragOver(e, n.id)} onDragLeave={() => setDropTarget(t => t === n.id ? null : t)} onDrop={e => onDrop(e, n)}>
          <button type="button" className={'twisty ' + (n.children.length ? '' : 'hidden')} onClick={e => { e.stopPropagation(); toggle(n.id); }} aria-label={open ? 'Restrânge' : 'Extinde'} tabIndex={-1}>
            <Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} />
          </button>
          <Icon name={active || open ? 'folderOpen' : 'folder'} className="fold" size={16} />
          <span className="name">{n.name}</span>
          {n.restricted && <Icon name="lock" className="lock" size={13} />}
          {n.documentCount > 0 && <span className="count">{n.documentCount}</span>}
        </div>
        {open && n.children.length > 0 && <ul>{n.children.map(c => renderNode(c, depth + 1))}</ul>}
      </li>
    );
  };

  const bulkSign = useBulkSign(docs, reload);
  return (
    <div className="repo">
      {bulkSign.modal}
      <aside className="repo-tree">
        <div className={'repo-tree-head ' + (folderId == null ? 'active' : '')} onClick={() => navigate('/repository')}><Icon name="layers" size={16} /><b>Repozitoriu</b></div>
        {tree.length === 0 ? <div className="muted small" style={{ padding: '8px 12px' }}>Niciun director</div> : <ul className="tree">{nodes.map(n => renderNode(n, 0))}</ul>}
      </aside>

      <section className="repo-main">
        <div className="crumbs" style={{ marginBottom: 12 }}>
          {folderId == null ? <b>Repozitoriu</b> : <a href="#/repository" onClick={e => { e.preventDefault(); navigate('/repository'); }}>Repozitoriu</a>}
          {current?.breadcrumb.map((b, i, arr) => (
            <React.Fragment key={b.id}>
              <Icon name="chevronRight" size={14} className="muted" />
              {i === arr.length - 1 ? <b>{b.name}</b> : <a href={'#/repository/' + b.id} onClick={e => { e.preventDefault(); navigate('/repository/' + b.id); }}>{b.name}</a>}
            </React.Fragment>
          ))}
          {current?.restricted && <span className="tag" title="Acces restricționat"><Icon name="lock" size={12} /> restricționat</span>}
        </div>

        <div className="toolbar" style={{ marginBottom: 16 }}>
          {canManage && <button className="btn" onClick={() => setDialog({ kind: 'new' })}><Icon name="folder" />Director nou</button>}
          {folderId != null && <button className="btn primary" onClick={() => setDialog({ kind: 'doc' })}><Icon name="plus" />Document nou</button>}
          {current && current.canManage && !current.system && <>
            <button className="btn" onClick={() => setDialog({ kind: 'rename' })}><Icon name="edit" />Redenumește</button>
            <button className="btn" onClick={() => setDialog({ kind: 'delete' })}><Icon name="trash" />Șterge</button>
          </>}
          {current && <button className="btn" onClick={() => setDialog({ kind: 'share' })}><Icon name="users" />Acces</button>}
          <span className="grow" />
          {loading && <span className="muted small">Se încarcă…</span>}
          {current && <span className="muted small">{current.folders.length} directoare · {current.documents.length} documente</span>}
          <button className="btn ghost sm" onClick={reload} title="Reîncarcă"><Icon name="refresh" /></button>
        </div>

        {subfolders.length > 0 && (
          <div className="folder-grid" style={{ marginBottom: 18 }}>
            {subfolders.map(f => (
              <div key={f.id} className={'folder-tile ' + (dropTarget === f.id ? 'drop' : '')} role="button" tabIndex={0} title={f.path}
                onClick={() => navigate('/repository/' + f.id)} onKeyDown={e => { if (e.key === 'Enter') navigate('/repository/' + f.id); }}
                onDragOver={e => onDragOver(e, f.id)} onDragLeave={() => setDropTarget(t => t === f.id ? null : t)} onDrop={e => onDrop(e, f)}>
                <Icon name="folder" />
                <div style={{ minWidth: 0 }}>
                  <b style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{f.name}{f.restricted && <Icon name="lock" size={12} className="muted" />}</b>
                  <span>{f.documentCount} {f.documentCount === 1 ? 'document' : 'documente'}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {folderId == null && subfolders.length === 0 && tree.length === 0 && (
          <EmptyState icon="folder" title="Niciun director" text="Repozitoriul nu conține încă directoare." action={canManage ? <button className="btn primary" onClick={() => setDialog({ kind: 'new' })}><Icon name="plus" />Director nou</button> : undefined} />
        )}

        {current && (
          current.documents.length === 0 ? (
            <EmptyState icon="folder" title="Director gol" text="Nu există documente în acest director."
              action={<button className="btn primary" onClick={() => setDialog({ kind: 'doc' })}><Icon name="plus" />Adaugă document</button>} />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    {bulkSign.head}
                    <Th k="reg" label="Nr. înreg." />
                    <Th k="title" label="Titlu" />
                    <Th k="type" label="Tip" />
                    <Th k="status" label="Status" options={[{ value: 'RECEIVED', label: 'Primit' }, { value: 'IN_WORK', label: 'În lucru' }, { value: 'RESOLVED', label: 'Rezolvat' }]} />
                    <Th k="assigned" label="Repartizat" />
                    <th style={{ width: 36 }} title="Fișier"><Icon name="file" size={14} /></th>
                    <Th k="created" label="Creat" />
                  </tr>
                </thead>
                <tbody>
                  {docs.map(d => (
                    <tr key={d.id} className={openId === d.id ? 'selected' : ''} onClick={() => onOpen(d.id)} draggable onDragStart={e => onDragStart(e, d)} title="Trageți peste un director pentru a muta">
                      {bulkSign.cell(d)}
                      <td className="num"><Stamp value={d.regFull} /></td>
                      <td title={d.title} style={{ maxWidth: 340 }}>{d.title || <span className="muted">(fără titlu)</span>}</td>
                      <td className="muted">{d.typeName}</td>
                      <td><StatusChip status={d.status} /></td>
                      <td>{d.assignedUserName ? <><span>{d.assignedUserName}</span><span className="muted"> · {d.assignedDepartmentName}</span></> : d.assignedDepartmentName || <span className="muted">—</span>}</td>
                      <td>{d.fileId && <Icon name="file" className="muted" size={16} />}</td>
                      <td className="num muted">{fmtWhen(d.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bulkSign.bar}
              {docs.length === 0 && <EmptyState icon="filter" title="Niciun document nu corespunde filtrelor" action={<button className="btn" onClick={() => setFilters({})}><Icon name="x" />Șterge filtrele</button>} />}
            </div>
          )
        )}
      </section>

      {dialog?.kind === 'new' && <NameDialog title="Director nou" label="Numele directorului" yes="Creează" onClose={() => setDialog(null)} onSubmit={createFolder} />}
      {dialog?.kind === 'rename' && current && <NameDialog title="Redenumește directorul" label="Nume nou" yes="Salvează" initial={current.name} onClose={() => setDialog(null)} onSubmit={renameFolder} />}
      {dialog?.kind === 'delete' && current && (
        <Confirm title="Șterge directorul" danger yesLabel="Șterge" onNo={() => setDialog(null)} onYes={deleteFolder}
          text={<>Directorul <b>{current.name}</b> va fi șters. Operația este posibilă doar dacă directorul este gol.</>} />
      )}
      {dialog?.kind === 'share' && current && (
        <Modal title={'Acces la „' + current.name + '”'} icon="users" onClose={() => setDialog(null)} footer={<><span className="grow" /><button className="btn" onClick={() => setDialog(null)}>Închide</button></>}>
          <SharePanel kind="repository/folders" id={current.id} canManage={current.canManage} />
        </Modal>
      )}
      {dialog?.kind === 'doc' && folderId != null && <DocumentForm folderId={folderId} onClose={() => setDialog(null)} onSaved={d => { setDialog(null); reload(); onOpen(d.id); }} />}
      {openId != null && <DocumentDrawer id={openId} onClose={() => onOpen(null)} onChanged={reload} />}
    </div>
  );
}

function NameDialog({ title, label, yes, initial, onClose, onSubmit }: { title: string; label: string; yes: string; initial?: string; onClose: () => void; onSubmit: (name: string) => Promise<void> }) {
  const toast = useToast();
  const [name, setName] = useState(initial || '');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try { await onSubmit(n); } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal title={title} icon="folder" size="sm" onClose={onClose} footer={<><span className="grow" /><button className="btn" onClick={onClose}>Renunță</button><button className="btn primary" disabled={busy || !name.trim()} onClick={submit}>{yes}</button></>}>
      <Field label={label} required>
        <input className="input" autoFocus value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }} maxLength={120} />
      </Field>
    </Modal>
  );
}
